import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Heart, User, CreditCard, Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { useIdValidation, formatSouthAfricanId } from "@/utils/saIdValidation";

export default function PatientSignup() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    identificationNumber: "",
    email: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // SA ID validation
  const idValidation = useIdValidation(formData.identificationNumber);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format ID number as user types
    if (name === 'identificationNumber') {
      // Remove non-digits and limit to 13 characters
      const cleanValue = value.replace(/\D/g, '').substring(0, 13);
      setFormData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    // Validate SA ID before submitting
    if (!idValidation.isValid && !idValidation.isEmpty) {
      setError(`Invalid South African ID number: ${idValidation.errors.join(', ')}`);
      setIsLoading(false);
      return;
    }

    try {
      await apiClient.post("/api/auth/patient/signup", formData);
      setSuccess("Patient information validated successfully! You can now proceed to login.");
      
      // Redirect to login page after a brief delay
      setTimeout(() => {
        navigate("/login", { 
          state: { 
            email: formData.email,
            message: "Patient signup completed. You can now request email verification." 
          }
        });
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to complete patient signup");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Heart className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Patient Signup
          </CardTitle>
          <p className="text-slate-600">
            Create your patient account to get started
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <label
                htmlFor="firstName"
                className="text-sm font-medium text-slate-700 flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                First Name
              </label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                required
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="lastName"
                className="text-sm font-medium text-slate-700 flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                Last Name
              </label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                required
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="identificationNumber"
                className="text-sm font-medium text-slate-700 flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                South African ID Number
                {formData.identificationNumber && (
                  idValidation.isValid ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : !idValidation.isEmpty && (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )
                )}
              </label>
              <div className="relative">
                <Input
                  id="identificationNumber"
                  name="identificationNumber"
                  type="text"
                  value={formData.identificationNumber}
                  onChange={handleChange}
                  placeholder="9001015009087"
                  required
                  maxLength={13}
                  className={`${
                    formData.identificationNumber && !idValidation.isEmpty
                      ? idValidation.isValid
                        ? 'border-green-300 focus:border-green-400'
                        : 'border-red-300 focus:border-red-400'
                      : ''
                  }`}
                />
                {formData.identificationNumber && formData.identificationNumber.length === 13 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-mono text-slate-500">
                    {formatSouthAfricanId(formData.identificationNumber)}
                  </div>
                )}
              </div>
              
              {/* Validation feedback */}
              {formData.identificationNumber && !idValidation.isEmpty && (
                <div className="space-y-1">
                  {idValidation.isValid ? (
                    <div className="text-xs text-green-600 space-y-1">
                      <p className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Valid South African ID number
                      </p>
                      {idValidation.details && (
                        <div className="ml-4 space-y-1 text-slate-600">
                          <p>Gender: {idValidation.details.gender}</p>
                          <p>Citizenship: {idValidation.details.citizenship}</p>
                          <p>Date of birth: {idValidation.details.dateOfBirth.day}/{idValidation.details.dateOfBirth.month}/{idValidation.details.dateOfBirth.year}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-red-600">
                      {idValidation.errors.map((error, index) => (
                        <p key={index} className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-slate-500">
                Enter your 13-digit South African ID number
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-slate-700 flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Email Address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                required
              />
              <p className="text-xs text-slate-500">
                We&apos;ll send you a verification code to this email
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || success || (formData.identificationNumber && !idValidation.isValid)}
            >
              {isLoading ? "Creating Account..." : "Complete Signup"}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-500"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
            
            <div className="pt-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Are you a healthcare provider?{" "}
                <Link 
                  to="/practice-owner-signup" 
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Register your practice
                </Link>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}